/** Typed wrappers around every API endpoint the UI uses. */

import { api } from '@/api/client';
import type {
  ActionDetail,
  ActionPreview,
  ActionRisk,
  ActionStatus,
  ActionSummary,
  AgentRunDetail,
  AgentRunSummary,
  DraftValuesResponse,
  LlmCallSummary,
  AiStatus,
  ApprovalDetail,
  AuditDetail,
  AuditSummary,
  CrawlDetail,
  CrawlPageSummary,
  CrawlSummary,
  DashboardResponse,
  ExecutionSummary,
  IntegrationSummary,
  IssueCategory,
  IssueDetail,
  IssueSeverity,
  IssueStatus,
  IssueSummary,
  MemberDetail,
  MeResponse,
  MessageResponse,
  OpportunityDetail,
  OrganizationDetail,
  Page,
  PropertyOption,
  RecommendationDetail,
  RecommendationSummary,
  SessionResponse,
  SiteDetail,
  SiteOnboardingStatus,
  SiteSummary,
  VerificationDetail,
} from '@/types/api';

// ---------------------------------------------------------------------- auth
export const authApi = {
  register: (body: {
    email: string;
    password: string;
    full_name: string;
    organization_name?: string;
  }) => api.post<SessionResponse>('/auth/register', body),

  login: (body: { email: string; password: string }) =>
    api.post<SessionResponse>('/auth/login', body),

  logout: () => api.post<MessageResponse>('/auth/logout'),

  me: () => api.get<MeResponse>('/auth/me'),

  changePassword: (body: { current_password: string; new_password: string }) =>
    api.post<MessageResponse>('/auth/change-password', body),

  forgotPassword: (body: { email: string }) =>
    api.post<MessageResponse>('/auth/forgot-password', body),
};

// -------------------------------------------------------------- organization
export const organizationApi = {
  current: () => api.get<OrganizationDetail>('/organizations/current'),
  update: (body: Partial<{ name: string; billing_email: string; is_agency: boolean }>) =>
    api.patch<OrganizationDetail>('/organizations/current', body),
  members: () => api.get<MemberDetail[]>('/organizations/current/members'),
  invite: (body: { email: string; role: string }) =>
    api.post<MemberDetail>('/organizations/current/members', body),
  updateMember: (membershipId: string, body: { role: string }) =>
    api.patch<MemberDetail>(`/organizations/current/members/${membershipId}`, body),
  removeMember: (membershipId: string) =>
    api.delete<MessageResponse>(`/organizations/current/members/${membershipId}`),
};

// --------------------------------------------------------------------- sites
export const siteApi = {
  list: (params?: { page?: number; page_size?: number; status?: string; search?: string }) =>
    api.get<Page<SiteSummary>>('/sites', params),

  get: (siteId: string) => api.get<SiteDetail>(`/sites/${siteId}`),

  create: (body: {
    name: string;
    base_url: string;
    target_country?: string | null;
    target_language?: string | null;
    business_description?: string | null;
    products_services?: string[];
    target_keywords?: string[];
  }) => api.post<SiteDetail>('/sites', body),

  update: (siteId: string, body: Record<string, unknown>) =>
    api.patch<SiteDetail>(`/sites/${siteId}`, body),

  archive: (siteId: string) => api.delete<MessageResponse>(`/sites/${siteId}`),

  onboarding: (siteId: string) =>
    api.get<SiteOnboardingStatus>(`/sites/${siteId}/onboarding`),

  dashboard: (siteId: string) => api.get<DashboardResponse>(`/sites/${siteId}/dashboard`),
};

// -------------------------------------------------------------------- crawls
export const crawlApi = {
  start: (siteId: string, body: { max_pages?: number; max_depth?: number } = {}) =>
    api.post<CrawlDetail>(`/sites/${siteId}/crawls`, body),

  list: (siteId: string, params?: { page?: number; page_size?: number; status?: string }) =>
    api.get<Page<CrawlSummary>>(`/sites/${siteId}/crawls`, params),

  get: (crawlId: string) => api.get<CrawlDetail>(`/crawls/${crawlId}`),

  cancel: (crawlId: string) => api.post<CrawlDetail>(`/crawls/${crawlId}/cancel`),

  pages: (
    crawlId: string,
    params?: {
      page?: number;
      page_size?: number;
      indexable?: boolean;
      status_code?: number;
      search?: string;
      orphans_only?: boolean;
      sort?: string;
    },
  ) => api.get<Page<CrawlPageSummary>>(`/crawls/${crawlId}/pages`, params),
};

// -------------------------------------------------------------------- audits
export const auditApi = {
  start: (siteId: string, body: { crawl_id?: string } = {}) =>
    api.post<AuditDetail>(`/sites/${siteId}/audits`, body),

  list: (siteId: string, params?: { page?: number; page_size?: number }) =>
    api.get<Page<AuditSummary>>(`/sites/${siteId}/audits`, params),

  get: (auditId: string) => api.get<AuditDetail>(`/audits/${auditId}`),
};

// -------------------------------------------------------------------- issues
export const issueApi = {
  list: (
    siteId: string,
    params?: {
      page?: number;
      page_size?: number;
      audit_id?: string;
      category?: IssueCategory;
      severity?: IssueSeverity;
      status?: IssueStatus;
      search?: string;
    },
  ) => api.get<Page<IssueSummary>>(`/sites/${siteId}/issues`, params),

  get: (issueId: string) => api.get<IssueDetail>(`/issues/${issueId}`),

  update: (issueId: string, body: { status: IssueStatus; reason?: string }) =>
    api.patch<IssueDetail>(`/issues/${issueId}`, body),
};

// ------------------------------------------------------------- opportunities
export const opportunityApi = {
  list: (
    siteId: string,
    params?: { page?: number; page_size?: number; type?: string; min_score?: number },
  ) => api.get<Page<OpportunityDetail>>(`/sites/${siteId}/opportunities`, params),
};

// ----------------------------------------------------------- recommendations
export const recommendationApi = {
  list: (
    siteId: string,
    params?: {
      page?: number;
      page_size?: number;
      status?: string;
      category?: IssueCategory;
      executable_only?: boolean;
    },
  ) => api.get<Page<RecommendationSummary>>(`/sites/${siteId}/recommendations`, params),

  get: (recommendationId: string) =>
    api.get<RecommendationDetail>(`/recommendations/${recommendationId}`),

  dismiss: (recommendationId: string, body: { reason?: string } = {}) =>
    api.post<RecommendationDetail>(`/recommendations/${recommendationId}/dismiss`, body),

  createActions: (recommendationId: string, body: { payloads?: Record<string, unknown>[] } = {}) =>
    api.post<ActionDetail[]>(`/recommendations/${recommendationId}/actions`, body),

  // Writes candidate copy and returns it for review. Deliberately does not
  // create the action: generated text reaches a page only after a person has
  // read it and approved the action built from it.
  draftValues: (recommendationId: string) =>
    api.post<DraftValuesResponse>(`/recommendations/${recommendationId}/draft-values`, {}),
};

// ------------------------------------------------------------------- actions
export const actionApi = {
  catalogue: () =>
    api.get<{ actions: Record<string, unknown>[]; note: string }>('/actions/catalogue'),

  queue: (params?: { page?: number; page_size?: number; status?: ActionStatus; risk?: ActionRisk }) =>
    api.get<Page<ActionSummary>>('/actions', params),

  listForSite: (
    siteId: string,
    params?: { page?: number; page_size?: number; status?: ActionStatus; type?: string },
  ) => api.get<Page<ActionSummary>>(`/sites/${siteId}/actions`, params),

  get: (actionId: string) => api.get<ActionDetail>(`/actions/${actionId}`),

  create: (siteId: string, body: Record<string, unknown>) =>
    api.post<ActionDetail>(`/sites/${siteId}/actions`, body),

  approve: (
    actionId: string,
    body: { comment?: string; edited_after_state?: Record<string, unknown> } = {},
  ) => api.post<ActionDetail>(`/actions/${actionId}/approve`, body),

  reject: (actionId: string, body: { reason?: string } = {}) =>
    api.post<ActionDetail>(`/actions/${actionId}/reject`, body),

  bulkApprove: (body: { action_ids: string[]; max_risk?: ActionRisk; comment?: string }) =>
    api.post<{ approved: string[]; skipped: { action_id: string; reason: string }[] }>(
      '/actions/bulk-approve',
      body,
    ),

  approvals: (actionId: string) => api.get<ApprovalDetail[]>(`/actions/${actionId}/approvals`),

  preview: (actionId: string) => api.get<ActionPreview>(`/actions/${actionId}/preview`),

  execute: (actionId: string) => api.post<ExecutionSummary>(`/actions/${actionId}/execute`),

  rollback: (actionId: string) => api.post<ExecutionSummary>(`/actions/${actionId}/rollback`),

  executions: (actionId: string) => api.get<ExecutionSummary[]>(`/actions/${actionId}/executions`),

  verifications: (actionId: string) =>
    api.get<VerificationDetail[]>(`/actions/${actionId}/verifications`),
};

// -------------------------------------------------------------- integrations
export const integrationApi = {
  list: (siteId?: string) => api.get<IntegrationSummary[]>('/integrations', { site_id: siteId }),

  googleStatus: () =>
    api.get<{ configured: boolean; redirect_uri: string; scopes: string[]; setup_note: string }>(
      '/integrations/google/status',
    ),

  connectGoogle: (body: { site_id: string; redirect_after?: string; setup?: boolean }) =>
    api.post<{ authorization_url: string }>('/integrations/google/connect', body),

  googleProperties: (integrationId: string) =>
    api.get<PropertyOption[]>(`/integrations/${integrationId}/google/properties`),

  selectGoogleProperty: (integrationId: string, body: { property_url: string }) =>
    api.post<IntegrationSummary>(`/integrations/${integrationId}/google/property`, body),

  syncGoogle: (integrationId: string, days = 28) =>
    api.post<{ queued: boolean; job_id: string | null }>(
      `/integrations/${integrationId}/google/sync`,
      undefined,
      { days },
    ),

  connectWordPress: (body: {
    site_id: string;
    base_url: string;
    username: string;
    application_password: string;
  }) => api.post<IntegrationSummary>('/integrations/wordpress/connect', body),

  connectGit: (body: {
    site_id: string;
    repository: string;
    access_token: string;
    base_branch?: string;
    content_root?: string;
  }) => api.post<IntegrationSummary>('/integrations/git/connect', body),

  disconnect: (integrationId: string) =>
    api.delete<MessageResponse>(`/integrations/${integrationId}`),
};

// ------------------------------------------------------------------------ ai
export const aiApi = {
  status: () => api.get<AiStatus>('/ai/status'),
  start: (siteId: string) => api.post<AgentRunSummary>(`/sites/${siteId}/ai-analysis`),
  list: (siteId: string, params?: { page?: number; page_size?: number }) =>
    api.get<Page<AgentRunSummary>>(`/sites/${siteId}/ai-analysis`, params),
  get: (runId: string) => api.get<AgentRunDetail>(`/ai-analysis/${runId}`),
  // Cost and metadata only — the prompt text is never stored, so this is safe
  // to show without leaking customer content or anything injected into it.
  calls: (runId: string) => api.get<LlmCallSummary[]>(`/ai-analysis/${runId}/calls`),
};
